/**
 * How a person is shown (product ruling 2026-10-04, db/README.md Revision 94): "each user can change profile image and
 * name but keep real name in parenthesis". Normative spec: shared/person-label/SPEC.md; must agree with
 * shared/person-label/test-vectors.json, as do the server (`PersonLabel.cs`) and the `app_user.display_label` column.
 *
 * The real name is `User.displayName` (also `realName`); the profile name is the person's own choice. Exports and official
 * documents are built by the server and use the real name only.
 */

export const MAX_PROFILE_NAME_LENGTH = 60;

/** "Profile name (Real name)" when the profile name is set and differs (ordinal) from the real name; else the real name. */
export function formatPersonLabel(realName: string, profileName: string | null | undefined): string {
  return !profileName || profileName === realName ? realName : `${profileName} (${realName})`;
}

/** The label of a user record: the server's `label` when present, else the same rule on its fields. */
export function userLabel(user: { displayName: string; profileName?: string | null; label?: string }): string {
  return user.label ?? formatPersonLabel(user.displayName, user.profileName);
}

function isForbidden(codePoint: number): boolean {
  return (
    codePoint <= 0x1f ||
    (codePoint >= 0x7f && codePoint <= 0x9f) ||
    codePoint === 0x2028 ||
    codePoint === 0x2029 ||
    (codePoint >= 0x202a && codePoint <= 0x202e) ||
    (codePoint >= 0x2066 && codePoint <= 0x2069)
  );
}

/**
 * The profile name to send, or null to clear it; `error` says why it would be refused. The server decides; this lets the
 * form say so before a round trip, with the same words.
 */
export function normalizeProfileName(input: string | null | undefined): { value: string | null; error: string | null } {
  if (input === null || input === undefined) return { value: null, error: null };
  const text = input.trim().normalize("NFC");
  if (text === "") return { value: null, error: null };
  let length = 0;
  for (const ch of text) {
    length++;
    if (isForbidden(ch.codePointAt(0) ?? 0)) {
      return { value: null, error: "The name cannot contain control characters, line breaks or text-direction marks." };
    }
  }
  if (length > MAX_PROFILE_NAME_LENGTH) return { value: null, error: `Use at most ${MAX_PROFILE_NAME_LENGTH} characters.` };
  return { value: text, error: null };
}

/** `userLabel` of a user that may not be in the caller's directory, else `fallback`. */
export function userLabelOr(user: Parameters<typeof userLabel>[0] | undefined | null, fallback: string): string {
  return user ? userLabel(user) : fallback;
}
